import Circles from "@/assets/img/today/moon/moon_diagram/circles.webp"
import Labels from "@/assets/img/today/moon/moon_diagram/moon_phase_labels.webp"
import Marker from "@/assets/img/today/moon/current_phase_marker.webp"

import { formatMoonDate } from "../../config/moon-content"
import type { MoonPhasesPayload } from "../../types"
import { MoonPhaseIcon, type MoonPhaseKey } from "./moon-phase-icon"

const SIZE = 260
const STROKE = 14.875
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/** Mean synodic month — turns moon age into cycle progress (0..1). */
const SYNODIC_MONTH_DAYS = 29.530588853

/** Whether the Moon has reached/crossed the phase at cycle `index` (0..7). */
function phaseReached(moonAge: number, index: number): boolean {
  const progress = Math.max(0, Math.min(1, moonAge / SYNODIC_MONTH_DAYS))
  return progress >= index / 8
}

/**
 * The eight phase icons on the ring (within a 340×340 box, matching the
 * original baked phase image). Cycle order: New at the bottom, waxing up the
 * left to Full at the top, waning down the right.
 */
const PHASE_POSITIONS: { phase: MoonPhaseKey; left: number; top: number }[] = [
  { phase: "new", left: 153, top: 306 },
  { phase: "waxC", left: 45, top: 261 },
  { phase: "q1", left: 0, top: 153 },
  { phase: "waxG", left: 45, top: 45 },
  { phase: "full", left: 153, top: 0 },
  { phase: "wanG", left: 261, top: 45 },
  { phase: "q3", left: 306, top: 153 },
  { phase: "wanC", left: 261, top: 261 },
]

interface MoonDiagramProps {
  diagram?: MoonPhasesPayload["diagram"]
}

/** Lunar-cycle journey diagram. Ported from MoonPhase MoonPhaseDiagram. */
export function MoonDiagram({ diagram }: MoonDiagramProps) {
  if (!diagram) return null

  const data = {
    phaseStart: diagram.phase_start,
    moonAge: diagram.moon_age_days,
    nextNewMoon: diagram.next_new_moon,
    nextFullMoon: diagram.next_full_moon,
    dates: {
      new: diagram.phases.new_moon,
      firstQuarter: diagram.phases.first_quarter,
      full: diagram.phases.full_moon,
      lastQuarter: diagram.phases.last_quarter,
    },
  }
  const degrees = (data.moonAge / 30) * 360
  const clamped = Math.max(0, Math.min(degrees, 360))
  const normalized = clamped >= 360 ? 359.999 : clamped
  const dashArray = `${(normalized / 360) * CIRCUMFERENCE} ${CIRCUMFERENCE}`

  return (
    <div className="px-4">
      <h3 className="mt-10 mb-2 font-display text-2xl font-medium text-purple-light">
        The Moon’s ongoing journey
      </h3>
      <p className="mb-6 font-body text-base text-purple-light/70">
        From New to Full and back again — see where the Moon is today in its celestial path.
      </p>

      <div className="relative flex w-full items-center justify-center">
        {/* Date labels around the ring */}
        <div className="relative h-[382px] w-[382px]">
          <div className="absolute inset-0 text-center font-body text-sm text-purple-light">
            {formatMoonDate(data.dates.full)}
          </div>
          <div className="absolute inset-0 rotate-90 text-center font-body text-sm text-purple-light">
            {formatMoonDate(data.dates.lastQuarter)}
          </div>
          <div className="absolute inset-0 flex items-end justify-center text-center font-body text-sm text-purple-light">
            {formatMoonDate(data.dates.new)}
          </div>
          <div className="absolute inset-0 -rotate-90 text-center font-body text-sm text-purple-light">
            {formatMoonDate(data.dates.firstQuarter)}
          </div>
        </div>

        {/* Progress ring */}
        <div className="absolute size-[260px] rotate-90">
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} fill="none">
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              stroke="rgba(149, 101, 253, 0.20)"
              strokeWidth={STROKE}
              strokeDasharray={dashArray}
              strokeLinecap="butt"
            />
          </svg>
        </div>

        {/* Center readout */}
        <div className="absolute flex flex-col items-center justify-center">
          <span className="text-center font-body text-sm text-purple-light/70">Phase Start</span>
          <span className="text-center font-body text-sm font-medium text-purple-light">
            {formatMoonDate(data.phaseStart)}
          </span>
          <div className="my-3 h-px w-full bg-purple-light/40" />
          <span className="text-center font-body text-sm text-purple-light/70">Moon Age</span>
          <span className="text-center font-body text-sm font-medium text-purple-light">
            {data.moonAge} days
          </span>
        </div>

        <img src={Labels} alt="" className="absolute size-[228px]" />
        <img src={Circles} alt="" className="absolute size-[306px]" />
        <div className="absolute size-[340px]">
          {PHASE_POSITIONS.map(({ phase, left, top }, index) => (
            <div key={phase} className="absolute size-[34px]" style={{ left, top }}>
              <MoonPhaseIcon
                phase={phase}
                active={phaseReached(data.moonAge, index)}
                className="size-[34px]"
              />
            </div>
          ))}
        </div>
        <img
          src={Marker}
          alt=""
          className="absolute size-[306px]"
          style={{ transform: `rotate(${degrees}deg)` }}
        />
      </div>

      <div className="-mt-[30px] flex justify-between">
        <div className="flex flex-col items-center justify-center">
          <MoonPhaseIcon phase="new" active className="size-[46px]" />
          <span className="mt-1 font-body text-sm text-purple-light/70">Next New Moon</span>
          <span className="text-center font-body text-sm font-medium text-purple-light">
            {formatMoonDate(data.nextNewMoon)}
          </span>
        </div>
        <div className="flex flex-col items-center justify-center">
          <MoonPhaseIcon phase="full" active className="size-[46px]" />
          <span className="mt-1 font-body text-sm text-purple-light/70">Next Full Moon</span>
          <span className="text-center font-body text-sm font-medium text-purple-light">
            {formatMoonDate(data.nextFullMoon)}
          </span>
        </div>
      </div>
    </div>
  )
}
